import { LitElement, html, css } from 'lit';
import { customElement, property, state, query } from 'lit/decorators.js';
import { api } from '../api/client';
import { setDeckTitle } from '../lib/page-title';
import type { Deck } from '../api/types';

function showToast(message: string) {
  const el = document.createElement('div');
  el.className = 'toast';
  el.textContent = message;
  document.body.appendChild(el);
  window.setTimeout(() => el.remove(), 2400);
}

/**
 * Full-bleed deck viewer. Loads a deck's metadata + HTML from the API
 * then renders it inside an <iframe srcdoc>. We use srcdoc rather than
 * a Blob URL so relative asset references inside the deck (e.g.
 * /src/deck-system.css) resolve against the parent origin — the Go
 * backend serves both the API and the static deck-system library at
 * the same root.
 *
 * Phase B (V3.2): the iframe sandbox dropped `allow-same-origin`, which
 * means in-iframe localStorage no longer persists. Theme is driven by
 * the parent via a `deck:theme` postMessage on every flip + on iframe
 * load. The in-iframe settings cog forwards Share intent to the parent
 * via a `deck:share` postMessage, which triggers the existing share()
 * flow here. The overlay Share button has been removed — only the Back
 * chevron remains above the iframe.
 */
@customElement('deck-viewer-page')
export class DeckViewerPage extends LitElement {
  @property({ attribute: 'deck-id' }) deckId = '';
  @state() private deck: Deck | null = null;
  @state() private error: string | null = null;
  @state() private sharing = false;
  @query('iframe') private iframeEl?: HTMLIFrameElement;

  static styles = css`
    :host {
      display: block;
      position: fixed;
      inset: 64px 0 0 0;
      background: var(--canvas);
    }
    iframe {
      width: 100%;
      height: 100%;
      border: 0;
      display: block;
    }
    /* No overlay back button — the top nav-bar's ← chevron covers it.
       Removing the overlay also clears the z-index:2 div that was
       sitting above the iframe and intercepting clicks at the top
       edge, so the deck's in-iframe theme-toggle + settings-cog at
       top-right become clickable again. */
    .state {
      padding: 64px 32px;
      text-align: center;
      color: var(--text-secondary);
    }
    .state.error { color: var(--danger); }
  `;

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('message', this.onMessage);
    window.addEventListener('themechange', this.onThemeChange);
    void this.load();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('message', this.onMessage);
    window.removeEventListener('themechange', this.onThemeChange);
  }

  updated(changed: Map<string, unknown>) {
    if (changed.has('deckId')) {
      void this.load();
    }
  }

  private onMessage = (e: MessageEvent) => {
    // Only handle messages from our own iframe
    if (!this.iframeEl || e.source !== this.iframeEl.contentWindow) return;
    const data = e.data;
    if (data && typeof data === 'object' && data.type === 'deck:share') {
      void this.share();
    }
  };

  private onThemeChange = (e: Event) => {
    const detail = (e as CustomEvent<{ theme: 'light' | 'dark' }>).detail;
    this.postThemeToIframe(detail?.theme ?? this.currentTheme());
  };

  private currentTheme(): 'light' | 'dark' {
    const t = document.documentElement.getAttribute('data-theme');
    return t === 'dark' ? 'dark' : 'light';
  }

  private postThemeToIframe(theme: 'light' | 'dark') {
    try {
      this.iframeEl?.contentWindow?.postMessage({ type: 'deck:theme', theme }, '*');
    } catch (e) { /* defensive */ }
  }

  private onIframeLoad = () => {
    // Send the current theme as soon as the iframe finishes loading so
    // the deck initialises in the right mode (no flash).
    this.postThemeToIframe(this.currentTheme());
    // Move keyboard focus into the iframe so F/T/S/?/← /→ shortcuts
    // (handled by deck-overlays.js inside the iframe) work without the
    // user first clicking inside.
    this.iframeEl?.focus();
  };

  private async load() {
    if (!this.deckId) {
      this.error = 'Missing deck id';
      return;
    }
    this.error = null;
    this.deck = null;
    setDeckTitle(undefined, false); // plain "Folio" until the title is known
    try {
      this.deck = await api.getDeck(this.deckId);
      setDeckTitle(this.deck.title);
    } catch (err) {
      this.error = err instanceof Error ? err.message : 'Failed to load deck';
    }
  }

  private async share() {
    if (!this.deck || this.sharing) return;
    this.sharing = true;
    try {
      const { url } = await api.createShare(this.deck.id);
      const full = url.startsWith('http') ? url : `${window.location.origin}${url}`;
      try {
        await navigator.clipboard.writeText(full);
        showToast('Share link copied');
      } catch {
        window.prompt('Copy the share link below', full);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Share failed';
      showToast(`Share failed: ${msg}`);
    } finally {
      this.sharing = false;
    }
  }

  render() {
    if (this.error) {
      return html`<div class="state error">Couldn't load deck — ${this.error}</div>`;
    }
    if (!this.deck) {
      return html`<div class="state">Loading deck…</div>`;
    }
    return html`
      <iframe
        title=${this.deck.title}
        srcdoc=${this.deck.html}
        sandbox="allow-scripts allow-popups allow-forms allow-modals"
        allow="fullscreen"
        @load=${this.onIframeLoad}
      ></iframe>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'deck-viewer-page': DeckViewerPage;
  }
}
